'use client'

import { useEffect, useRef, useState } from 'react'

type RemoteImageProps = {
  src?: string
  alt: string
  className?: string
  /** Fired once the image is on screen, or immediately if it never will be. */
  onLoad?: () => void
}

/**
 * Artwork that is not part of the cat collection (Helium hotspots), loaded from
 * whatever URL the NFT metadata points at. Those URLs go stale, so a failed
 * load falls back to a placeholder rather than a broken image, and the
 * dimensions are declared up front so the grid does not jump while it loads.
 */
export default function RemoteImage({
  src,
  alt,
  className,
  onLoad,
}: RemoteImageProps) {
  const [failed, setFailed] = useState(false)
  const ref = useRef<HTMLImageElement>(null)
  const unavailable = !src || failed

  // A cached image can finish decoding before React attaches the handler.
  useEffect(() => {
    if (unavailable || ref.current?.complete) onLoad?.()
  }, [unavailable, onLoad])

  if (unavailable) {
    return (
      <div
        className={`flex aspect-square w-full items-center justify-center bg-dark-7 px-2 text-gray-4 ${className || ''}`}
        role="img"
        aria-label={`${alt} (artwork unavailable)`}
      >
        <span className="truncate text-xs font-bold">{alt}</span>
      </div>
    )
  }

  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      ref={ref}
      src={src}
      alt={alt}
      width={1000}
      height={1000}
      loading="lazy"
      decoding="async"
      className={className}
      style={{ width: '100%', height: 'auto', display: 'block' }}
      onLoad={onLoad}
      onError={() => setFailed(true)}
    />
  )
}
